import React, { useMemo } from 'react';
import { Text } from '@airwallex/typography';
import { formatCurrency } from '@airwallex/awx-currency-formatter';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { DiscountInfo } from '@/types/discount';
import { buildDiscountSubtext } from '@/utils/discount';

type RowProps = React.HTMLAttributes<HTMLDivElement>;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StackedRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

type DiscountRowVariant = 'row' | 'stacked';

interface DiscountRowsProps {
  discounts?: DiscountInfo[];
  currency: string;
  variant?: DiscountRowVariant;
}

const DiscountRows: React.FC<DiscountRowsProps> = ({ discounts, currency, variant = 'row' }) => {
  const { t } = useTranslation();
  const RowComponent: React.ElementType<RowProps> = variant === 'stacked' ? StackedRow : Row;

  const rows = useMemo(() => {
    if (!discounts || discounts.length === 0) return null;

    return discounts.map((discount) => {
      const subText = buildDiscountSubtext(discount, t);

      return (
        <RowComponent key={discount.discount.id}>
          <Text variant="body">
            {discount.discount.name}
            {subText ? ` (${subText})` : ''}
          </Text>
          <Text variant="body">
            -
            {formatCurrency({
              amount: discount.amount,
              currency,
              showSymbol: true,
              showCode: false,
            })}
          </Text>
        </RowComponent>
      );
    });
  }, [RowComponent, currency, discounts, t]);

  return <>{rows}</>;
};

export default DiscountRows;
